import "./RunABCDModal.scss";
import { ABCDCompanyOptions } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCompanyOptions";
import { ABCDCreative } from "@blisspointmedia/bpm-types/dist/ABCD";
import { kickOffABCDRunner, updateABCDCreative } from "../ABCD/ABCDUtils";
import { MdCancel, MdClose, MdInfoOutline } from "react-icons/md";
import { Modal, Tooltip } from "react-bootstrap";
import { useSetError } from "../../redux/modals";
import * as R from "ramda";
import * as uuid from "uuid";
import React, { useCallback, useMemo, useState } from "react";
import useLocation from "../../utils/hooks/useLocation";
import {
  Button,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  Img,
  OverlayTrigger,
  Spinner,
} from "../../Components";

interface RunABCDModalProps {
  abcdCreativeMap: Record<string, ABCDCreative>;
  abcdCompanyOptions: ABCDCompanyOptions;
  onHide: Function;
  selectedCreativeID: string;
}

const RunABCDModal = ({
  abcdCompanyOptions,
  abcdCreativeMap = {},
  onHide,
  selectedCreativeID,
}: RunABCDModalProps): JSX.Element => {
  const [brand, setBrand] = useState<string>(
    abcdCreativeMap &&
      selectedCreativeID &&
      abcdCreativeMap[selectedCreativeID] &&
      abcdCreativeMap[selectedCreativeID].brand_name
      ? abcdCreativeMap[selectedCreativeID].brand_name
      : ""
  );
  const [brandVariations, setBrandVariations] = useState<string[]>(
    abcdCreativeMap &&
      selectedCreativeID &&
      abcdCreativeMap[selectedCreativeID] &&
      abcdCreativeMap[selectedCreativeID].brand_variations
      ? abcdCreativeMap[selectedCreativeID].brand_variations
      : []
  );
  const [callToActions, setCallToActions] = useState<string[]>(
    abcdCreativeMap &&
      selectedCreativeID &&
      abcdCreativeMap[selectedCreativeID] &&
      abcdCreativeMap[selectedCreativeID].call_to_actions
      ? abcdCreativeMap[selectedCreativeID].call_to_actions
      : []
  );
  const [duplicateCreativeIDs, setDuplicateCreativeIDs] = useState<string[]>([]);
  const [productCategories, setProductCategories] = useState<string[]>(
    abcdCreativeMap &&
      selectedCreativeID &&
      abcdCreativeMap[selectedCreativeID] &&
      abcdCreativeMap[selectedCreativeID].products_categories
      ? abcdCreativeMap[selectedCreativeID].products_categories
      : []
  );
  const [products, setProducts] = useState<string[]>(
    abcdCreativeMap &&
      selectedCreativeID &&
      abcdCreativeMap[selectedCreativeID] &&
      abcdCreativeMap[selectedCreativeID].products
      ? abcdCreativeMap[selectedCreativeID].products
      : []
  );
  const [runningABCD, setRunningABCD] = useState<boolean>(false);
  const { company } = useLocation();
  const setError = useSetError();

  const runABCDIngestionJob = useCallback(
    (creative: ABCDCreative) => {
      (async () => {
        try {
          setRunningABCD(true);
          await updateABCDCreative(creative);
          if (duplicateCreativeIDs && duplicateCreativeIDs.length) {
            for (const creativeID of R.keys(abcdCreativeMap)) {
              await updateABCDCreative({
                ...abcdCreativeMap[creativeID],
                creative_id: creativeID,
                filename: creative.filename,
                brand_name: creative.brand_name,
                brand_variations: creative.brand_variations,
                products: creative.products,
                products_categories: creative.products_categories,
                call_to_actions: creative.call_to_actions,
              });
            }
          }
          await kickOffABCDRunner(creative);
          setError({
            message: `Sucessfully started ABCD for ${creative.creative_id}`,
            title: "Running ABCD!",
            variant: "success",
          });
        } catch (e) {
          setError({
            message: `There was an error trying to run abcd for ${creative.creative_name}. Error: ${e}`,
            title: "ABCD Runner Error",
          });
        } finally {
          setRunningABCD(false);
        }
      })();
    },
    [abcdCreativeMap, duplicateCreativeIDs, setError]
  );

  // Given the creative ID we selected, we need to pick out all duplicates in the abcd creative map
  const selectedCreatives = useMemo(() => {
    const selectedCreatives: Record<string, string[]> = {};
    if (selectedCreativeID) {
      for (const creativeID of R.keys(abcdCreativeMap)) {
        const creative = abcdCreativeMap[creativeID];
        if (
          (creative.filename && creative.filename.split(".")[0] === selectedCreativeID) ||
          duplicateCreativeIDs.includes(creative.creative_id)
        ) {
          selectedCreatives[creative.creative_name] = [
            ...R.defaultTo([], selectedCreatives[creative.creative_name]),
            creative.creative_id,
          ];
        }
      }
    }
    return selectedCreatives;
  }, [abcdCreativeMap, duplicateCreativeIDs, selectedCreativeID]);

  const [
    brandOptions,
    brandVariationOptions,
    productOptions,
    productCategoryOptions,
    callToActionOptions,
  ] = useMemo(() => {
    if (abcdCompanyOptions) {
      return [
        abcdCompanyOptions.brand_name.sort(),
        abcdCompanyOptions.brand_variations.sort(),
        abcdCompanyOptions.products.sort(),
        abcdCompanyOptions.products_categories.sort(),
        abcdCompanyOptions.call_to_actions.sort(),
      ];
    }
    return [[], [], [], [], []];
  }, [abcdCompanyOptions]);

  const modalHeader = (
    <Modal.Header>
      <div className="titleContainer">
        <Modal.Title>Run Best Practice Tagging</Modal.Title>
      </div>
      <div className="closeContainer" onClick={() => onHide()}>
        <MdClose />
      </div>
    </Modal.Header>
  );
  const modalBody = (
    <Modal.Body>
      <div className="modalInfoContainer">
        <div className="creativeInfoContainer">
          Use Google’s Gemini AI technology to tag
          <br /> your video with <span className="creativeInfo">key creative elements</span>{" "}
          <OverlayTrigger
            overlay={
              <Tooltip id={uuid.v4()}>
                These key creative elements are based on Google’s ABCD framework, which helps
                optimize video ads for better engagement and performance.
              </Tooltip>
            }
          >
            <MdInfoOutline />
          </OverlayTrigger>
          <br />
          that drive video performance.
        </div>
        <div className="googleInfoContainer">
          <div className="googleInfo">
            Using{" "}
            <a
              className="googleInfoLink"
              href="https://business.google.com/us/think/future-of-marketing/youtube-video-ad-creative/"
              rel="noopener noreferrer"
              target="_blank"
            >
              research
            </a>{" "}
            by:
          </div>
          <Img src={"https://cdn.blisspointmedia.com/assets/img/Google.png"} />
        </div>
      </div>
      <div className="modalCreativeContainer">
        <div className="section">
          <div className="header">Creative Name(s) and ISCI(s)</div>
          <div className="selectedCreativesContainer">
            {R.map(creativeName => {
              return (
                <div className="selectedCreative">
                  <div className="creativeName">{creativeName}</div>
                  {R.map(
                    creativeID => (
                      <div className="creativeID">
                        <div className="text">{creativeID}</div>
                        {duplicateCreativeIDs.includes(creativeID) && (
                          <div
                            className="icon"
                            onClick={() =>
                              setDuplicateCreativeIDs(
                                R.filter(id => id !== creativeID, duplicateCreativeIDs)
                              )
                            }
                          >
                            <MdCancel />
                          </div>
                        )}
                      </div>
                    ),
                    selectedCreatives[creativeName]
                  )}
                </div>
              );
            }, R.keys(selectedCreatives))}
          </div>
          <div className="duplicateDropdown">
            <Dropdown
              label={"Add Duplicative ISCIs"}
              onChange={option =>
                setDuplicateCreativeIDs(current => {
                  if (current.includes(option)) {
                    return current.filter(item => item !== option);
                  }
                  return R.uniq([...current, option]).sort();
                })
              }
              options={R.map(
                creative_id => ({
                  label: `${creative_id} ${abcdCreativeMap[creative_id].creative_name}`,
                  value: creative_id,
                }),
                R.keys(abcdCreativeMap)
              )}
              type={DropdownToggleType.FILLED}
            />
          </div>
        </div>
        <div className="section">
          <div className="header">Video Details</div>
          <div className="subheader">Brand</div>
          <div className="dropdown">
            <Dropdown
              autofill
              onChange={option => setBrand(option)}
              options={brandOptions}
              placeholder="Select Brand"
              type={DropdownToggleType.OUTLINED}
              value={brand}
            />
          </div>
          <div className="subheader">Brand Variations</div>
          <div className="dropdown">
            <Dropdown
              autofill
              onChange={option =>
                setBrandVariations(current => {
                  if (current.includes(option)) {
                    return current.filter(item => item !== option);
                  }
                  return R.uniq([...current, option]).sort();
                })
              }
              options={brandVariationOptions}
              placeholder="Select Brand Variations"
              type={DropdownToggleType.OUTLINED}
              value={brandVariations.join(", ")}
            />
          </div>
          <div className="subheader">Product Name</div>
          <div className="dropdown">
            <Dropdown
              autofill
              onChange={option =>
                setProducts(current => {
                  if (current.includes(option)) {
                    return current.filter(item => item !== option);
                  }
                  return R.uniq([...current, option]).sort();
                })
              }
              options={productOptions}
              placeholder="Select Products"
              type={DropdownToggleType.OUTLINED}
              value={products.join(", ")}
            />
          </div>
          <div className="subheader">Product Category</div>
          <div className="dropdown">
            <Dropdown
              autofill
              onChange={option =>
                setProductCategories(current => {
                  if (current.includes(option)) {
                    return current.filter(item => item !== option);
                  }
                  return R.uniq([...current, option]).sort();
                })
              }
              options={productCategoryOptions}
              placeholder="Select Product Categories"
              type={DropdownToggleType.OUTLINED}
              value={productCategories.join(", ")}
            />
          </div>
          <div className="subheader">
            Call to Actions <MdInfoOutline />
          </div>
          <div className="dropdown">
            <Dropdown
              autofill
              onChange={option =>
                setCallToActions(current => {
                  if (current.includes(option)) {
                    return current.filter(item => item !== option);
                  }
                  return R.uniq([...current, option]).sort();
                })
              }
              options={callToActionOptions}
              placeholder="Select Call to Actions"
              type={DropdownToggleType.OUTLINED}
              value={callToActions.join(", ")}
            />
          </div>
        </div>
      </div>
    </Modal.Body>
  );

  const modalFooter = (
    <Modal.Footer>
      <Button
        className="cancelButton"
        design="secondary"
        onClick={() => onHide()}
        size={"sm"}
        type={ButtonType.OUTLINED}
      >
        Cancel
      </Button>
      {runningABCD ? (
        <Spinner />
      ) : (
        <Button
          className="saveAndApplyButton"
          onClick={() => {
            const creativeToRun: ABCDCreative = {
              ...abcdCreativeMap[selectedCreativeID],
              company,
              creative_id: selectedCreativeID,
              filename: abcdCreativeMap[selectedCreativeID]
                ? abcdCreativeMap[selectedCreativeID].filename
                : `${selectedCreativeID}.mp4`,
              brand_name: brand,
              brand_variations: brandVariations,
              products: products,
              products_categories: productCategories,
              call_to_actions: callToActions,
            };
            if (
              R.isEmpty(creativeToRun.brand_name) ||
              R.isEmpty(creativeToRun.brand_variations) ||
              R.isEmpty(creativeToRun.products) ||
              R.isEmpty(creativeToRun.products_categories) ||
              R.isEmpty(creativeToRun.call_to_actions)
            ) {
              setError({
                message: `There is an error with the creative you are trying to run ABCD on; make sure that your creative has no missing fields.
              Creative: ${JSON.stringify(creativeToRun)}`,
                title: "Creative Setting Error",
              });
            } else {
              runABCDIngestionJob(creativeToRun);
            }
          }}
          size={"sm"}
          type={ButtonType.FILLED}
        >
          Run Tagging
        </Button>
      )}
    </Modal.Footer>
  );

  return (
    <Modal
      centered
      className={"runABCDModal"}
      onHide={() => onHide()}
      show
      size="lg"
      aria-hidden="false"
    >
      {modalHeader}
      {modalBody}
      {modalFooter}
    </Modal>
  );
};

export default RunABCDModal;
